import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
} from "@mui/x-data-grid";
import { itIT } from "@mui/x-data-grid/locales";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import AddIcon from "@mui/icons-material/Add";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAuth } from "../contexts/AuthContext";
import { ConfirmDialog } from "../components/common";
import {
  WaterBillClient,
  WaterBillClientInput,
  apiErrorMessage,
  createWaterBillClient,
  deleteWaterBillClient,
  listWaterBillClients,
  updateWaterBillClient,
} from "../services/waterBillsApi";

const QUERY_KEY = "water-bill-clients";

type FormState = Record<keyof WaterBillClientInput, string>;

const EMPTY_FORM: FormState = {
  code: "",
  site_name: "",
  cdc: "",
  company_name: "",
  supplier: "",
  city: "",
  province: "",
  meter_serial: "",
  notes: "",
};

const FORM_FIELDS: { key: keyof FormState; label: string; helper?: string; multiline?: boolean }[] = [
  {
    key: "code",
    label: "Codice cliente / servizio",
    helper:
      "Il codice che identifica la sede in bolletta. Se la bolletta ne riporta due (es. cliente e utenza) scrivili separati da '/', come '503863428/1123877'.",
  },
  { key: "site_name", label: "Nome sede" },
  { key: "cdc", label: "CDC" },
  { key: "company_name", label: "Ragione sociale" },
  { key: "supplier", label: "Fornitore" },
  { key: "city", label: "Comune" },
  { key: "province", label: "Provincia" },
  { key: "meter_serial", label: "Matricola contatore", multiline: true },
  { key: "notes", label: "Note", multiline: true },
];

const toForm = (c: WaterBillClient): FormState => ({
  code: c.code || "",
  site_name: c.site_name || "",
  cdc: c.cdc || "",
  company_name: c.company_name || "",
  supplier: c.supplier || "",
  city: c.city || "",
  province: c.province || "",
  meter_serial: c.meter_serial || "",
  notes: c.notes || "",
});

export const WaterBillClientsSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const hasPermission = user?.role === "admin" || user?.role === "mantainer";

  const [search, setSearch] = React.useState("");
  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const { data = [], isLoading, error } = useQuery(QUERY_KEY, listWaterBillClients, {
    enabled: hasPermission,
  });

  const [formOpen, setFormOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<FormState>(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = React.useState<WaterBillClient | null>(null);

  React.useEffect(() => {
    setPaginationModel((p) => ({ ...p, page: 0 }));
  }, [search]);

  const saveMutation = useMutation(
    (payload: { id: string | null; data: WaterBillClientInput }) =>
      payload.id ? updateWaterBillClient(payload.id, payload.data) : createWaterBillClient(payload.data),
    {
      onSuccess: (_d, vars) => {
        queryClient.invalidateQueries(QUERY_KEY);
        toast.success(vars.id ? "Cliente aggiornato" : "Cliente creato");
        setFormOpen(false);
      },
      onError: (err: any) => {
        toast.error(apiErrorMessage(err, "Salvataggio non riuscito"));
      },
    }
  );

  const deleteMutation = useMutation(deleteWaterBillClient, {
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY);
      toast.success("Cliente eliminato");
      setDeleteTarget(null);
    },
    onError: (err: any) => {
      toast.error(apiErrorMessage(err, "Eliminazione non riuscita"));
      setDeleteTarget(null);
    },
  });

  const rows = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((c) =>
      [c.code, c.site_name, c.cdc, c.company_name, c.supplier, c.city, c.province, c.meter_serial, c.notes]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [data, search]);

  if (!hasPermission) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Non hai i permessi per accedere a questa sezione. Solo gli amministratori e i mantainer
          possono gestire i clienti.
        </Alert>
      </Box>
    );
  }

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (row: WaterBillClient) => {
    setEditingId(row.id);
    setForm(toForm(row));
    setFormOpen(true);
  };

  const handleSave = () => {
    if (!form.code.trim()) {
      toast.error("Il codice è obbligatorio");
      return;
    }
    saveMutation.mutate({ id: editingId, data: form });
  };

  const columns: GridColDef[] = [
    { field: "code", headerName: "Codice cliente / servizio", width: 210 },
    { field: "site_name", headerName: "Nome sede", width: 190 },
    { field: "cdc", headerName: "CDC", width: 90 },
    { field: "company_name", headerName: "Ragione sociale", width: 170 },
    { field: "supplier", headerName: "Fornitore", width: 190 },
    { field: "city", headerName: "Comune", width: 150 },
    { field: "province", headerName: "PR", width: 60 },
    { field: "notes", headerName: "Note", width: 180 },
    {
      field: "actions",
      type: "actions",
      headerName: "Azioni",
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label="Modifica"
          onClick={() => openEdit(params.row as WaterBillClient)}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon />}
          label="Elimina"
          onClick={() => setDeleteTarget(params.row as WaterBillClient)}
        />,
      ],
    },
  ];

  return (
    <Box sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          mb: 2,
          flexShrink: 0,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 1000 }}>
          Anagrafica dei clienti usata per scegliere il codice da scrivere nella colonna CODICE
          CLIENTE dell'Excel. Una bolletta riporta sia il codice cliente sia il codice utenza: nel
          foglio finisce quello presente in questa tabella. Per un nuovo cliente aggiungi qui il
          codice che lo identifica.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ flexShrink: 0 }}>
          Nuovo cliente
        </Button>
      </Box>

      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <TextField
          size="small"
          placeholder="Cerca per codice, sede, fornitore…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {Boolean(error) && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          Errore nel caricamento dei dati.
        </Alert>
      )}

      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          sx={{ borderRadius: 2 }}
          localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
        />
      </Box>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {FORM_FIELDS.map((f) => (
              <TextField
                key={f.key}
                label={f.label}
                size="small"
                fullWidth
                required={f.key === "code"}
                multiline={f.multiline}
                minRows={f.multiline ? 2 : undefined}
                value={form[f.key]}
                helperText={f.helper}
                onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setFormOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleSave} disabled={saveMutation.isLoading}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        loading={deleteMutation.isLoading}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Conferma eliminazione"
        content={`Eliminare il cliente con codice "${deleteTarget?.code}"?`}
        confirmText="Elimina"
        cancelText="Annulla"
      />
    </Box>
  );
};
